import { useState, useEffect, useCallback } from 'react';

const PM_KEY = 'pm_stepup_verified_at';
const CUSTOMER_KEY = 'customer_sca_verified_at';
const WARN_BEFORE_MS = 60_000; // show warning 60s before expiry

export type SCAStatus = 'fresh' | 'expiring' | 'expired' | 'none';

/**
 * Reactive SCA freshness tracker.
 * Polls sessionStorage every second and returns the current status + seconds remaining.
 *
 * @param storageKey  - The sessionStorage key holding the ISO timestamp
 * @param windowMs    - The step-up validity window in ms (default 300 000 = 5 min)
 */
export function useSCAFreshness(
  storageKey: typeof PM_KEY | typeof CUSTOMER_KEY | string,
  windowMs = 300_000,
) {
  const getStatus = useCallback((): { status: SCAStatus; secondsLeft: number } => {
    const ts = sessionStorage.getItem(storageKey);
    if (!ts) return { status: 'none', secondsLeft: 0 };

    const elapsed = Date.now() - new Date(ts).getTime();
    const remaining = windowMs - elapsed;

    if (remaining <= 0) return { status: 'expired', secondsLeft: 0 };
    if (remaining <= WARN_BEFORE_MS) return { status: 'expiring', secondsLeft: Math.ceil(remaining / 1000) };
    return { status: 'fresh', secondsLeft: Math.ceil(remaining / 1000) };
  }, [storageKey, windowMs]);

  const [state, setState] = useState(getStatus);

  useEffect(() => {
    // Poll every second while a step-up exists
    const id = setInterval(() => setState(getStatus()), 1_000);
    return () => clearInterval(id);
  }, [getStatus]);

  return state;
}

export { PM_KEY as PM_SCA_KEY, CUSTOMER_KEY as CUSTOMER_SCA_KEY };
